import { getNoteIndex } from './enharmonicUtils.js';
import { parseChordString } from './chordParser.js';
import { buildChordString } from './chordBuilder.js';
import { normalizeToV3, isChordString } from './chordModelMigration.js';
import { formatNote } from './noteFormatting.js';
import { parseNote, parseKeySignature } from './noteMigration.js';
import { isKeySignature } from './noteTypes.js';
/**
 * Accidental spelling preference implied by a key signature: 'flat' for keys
 * whose signature carries flats (F, B♭, E♭, ... / Dm, Gm, Cm, ...), 'sharp'
 * for keys whose signature carries sharps (G, D, A, ... / Em, Bm, F♯m, ...),
 * and null for C major / A minor (no accidentals — no preference).
 *
 * Used by transposition to spell transposed notes the way the destination
 * key would (transposing C→E♭ should yield A♭, not G♯).
 *
 * @param key - Key as a string ("E♭", "F♯m", ASCII accepted) or KeySignature
 * @returns 'flat', 'sharp', or null (no preference / unrecognized key)
 */
export const getKeyAccidentalPreference = (key) => {
    if (!key) {
        return null;
    }
    let keySig;
    if (isKeySignature(key)) {
        keySig = key;
    }
    else if (typeof key === 'string') {
        try {
            keySig = parseKeySignature(key);
        }
        catch {
            return null;
        }
    }
    else {
        return null;
    }
    const { note, mode } = keySig;
    // Accidental roots carry their own signature side (B♭ major → flats,
    // F♯ minor → sharps), including the enharmonic tritone keys.
    if (note.modifier === 'f')
        return 'flat';
    if (note.modifier === 's')
        return 'sharp';
    // Natural roots by circle of fifths
    if (mode === 'maj') {
        if (note.noteName === 'F')
            return 'flat';
        if (note.noteName === 'C')
            return null;
        return 'sharp'; // G, D, A, E, B
    }
    if (['D', 'G', 'C', 'F'].includes(note.noteName))
        return 'flat';
    if (note.noteName === 'A')
        return null;
    return 'sharp'; // E, B minor
};
/**
 * Transposes a slot by a given number of semitones.
 * Custom chords (slots with customText) are returned unchanged.
 * @param slot The slot to transpose
 * @param semitones The number of semitones to transpose (positive = up, negative = down)
 * @param targetKey Optional destination key; when given, transposed notes are
 *                  spelled to match its accidental preference
 * @returns The transposed slot
 */
export const transposeSlot = (slot, semitones, targetKey) => {
    // Custom chords are not transposed - return unchanged
    if (slot.customText) {
        return slot;
    }
    // If no chord, return unchanged
    if (!slot.chord) {
        return slot;
    }
    const v3Chord = normalizeToV3(slot.chord);
    if (!v3Chord.root) {
        return slot;
    }
    // Transpose the chord using the new function
    const transposedChord = transposeChord(v3Chord, semitones, targetKey);
    // Return the slot with the transposed chord
    return {
        ...slot,
        chord: transposedChord,
    };
};
export function transposeChord(chord, semitones, targetKey) {
    // Handle string input (legacy API)
    if (isChordString(chord)) {
        // Empty or placeholder strings
        if (!chord || chord.trim() === '' || chord === '- - -') {
            return chord;
        }
        // Parse string to chord model
        const parsedChord = parseChordString(chord);
        // If parsing failed (null fundamental), return original
        if (!parsedChord.root) {
            return chord;
        }
        // Transpose using internal function
        const transposedV2 = transposeChordModel(parsedChord, semitones, targetKey);
        // Build back to string
        return buildChordString(transposedV2);
    }
    // Handle object input (V1 or V2)
    return transposeChordModel(chord, semitones, targetKey);
}
/**
 * Internal function that transposes a chord model.
 *
 * Accepts V1, V2, and V3 chord models.
 * Always returns V3 format with transposed Note objects.
 *
 * @param chord - Chord model in any format
 * @param semitones - The number of semitones to transpose
 * @returns V3 format chord model with transposed notes
 */
const transposeChordModel = (chord, semitones, targetKey) => {
    // Normalize to V3 for internal processing
    const v3 = normalizeToV3(chord);
    // Handle null root
    if (!v3.root) {
        return v3;
    }
    const preference = getKeyAccidentalPreference(targetKey);
    // Transpose the root
    const transposedRoot = transposeNote(v3.root, semitones, preference);
    // Transpose the bass note if it exists
    let transposedBass = null;
    if (v3.bass) {
        transposedBass = transposeNote(v3.bass, semitones, preference);
    }
    // Return V3 chord model with transposed notes
    return {
        root: transposedRoot,
        quality: v3.quality,
        extension: v3.extension,
        isDominant: v3.isDominant,
        adds: [...v3.adds],
        alterations: [...v3.alterations],
        bass: transposedBass,
    };
};
/**
 * Transposes a note by a given number of semitones.
 *
 * Spelling of the result: the destination key's accidental preference wins
 * when given (an E♭ chart spells its notes with flats regardless of how the
 * source note was spelled); otherwise the original note's accidental is
 * kept, and natural notes fall back to sharps ascending / flats descending.
 *
 * @param note - The Note object to transpose
 * @param semitones - The number of semitones to transpose
 * @param preference - Accidental preference of the destination key, or null
 * @returns The transposed Note object
 */
const transposeNote = (note, semitones, preference = null) => {
    // Identity transposition (0, ±12, ...) preserves the original spelling,
    // including uncommon ones (C♭, B♯, E♯, F♭) absent from the chromatic
    // scales below, which would otherwise be normalized away.
    if (((semitones % 12) + 12) % 12 === 0) {
        return note;
    }
    const noteName = formatNote(note);
    const noteIndex = getNoteIndex(noteName);
    if (noteIndex === -1) {
        return note; // Return unchanged if invalid
    }
    const newIndex = (noteIndex + semitones + 12) % 12;
    const chromaticScale = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
    const chromaticScaleFlats = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];
    // Determine if the original note uses flats or sharps
    const usesFlat = note.modifier === 'f';
    const usesSharp = note.modifier === 's';
    let transposedNoteName;
    if (preference === 'flat') {
        transposedNoteName = chromaticScaleFlats[newIndex];
    }
    else if (preference === 'sharp') {
        transposedNoteName = chromaticScale[newIndex];
    }
    else if (usesFlat) {
        transposedNoteName = chromaticScaleFlats[newIndex];
    }
    else if (usesSharp) {
        transposedNoteName = chromaticScale[newIndex];
    }
    else {
        // For natural notes, use the context to decide
        // In most cases, prefer sharps for ascending and flats for descending
        if (semitones > 0) {
            transposedNoteName = chromaticScale[newIndex];
        }
        else {
            transposedNoteName = chromaticScaleFlats[newIndex];
        }
    }
    // Parse the transposed note name back to a Note object
    return parseNote(transposedNoteName);
};
/**
 * Calculates the semitone difference between two keys.
 * @param fromKey The source key (e.g., "C", "Gm", "F#")
 * @param toKey The target key (e.g., "G", "Dm", "B♭")
 * @returns The number of semitones to transpose (+/- 11)
 */
export const getKeyTranspositionInterval = (fromKey, toKey) => {
    if (!fromKey || !toKey) {
        return 0;
    }
    // Extract the root note from the key (remove 'm' suffix if present)
    const fromRoot = fromKey.endsWith('m') ? fromKey.slice(0, -1) : fromKey;
    const toRoot = toKey.endsWith('m') ? toKey.slice(0, -1) : toKey;
    const fromIndex = getNoteIndex(fromRoot);
    const toIndex = getNoteIndex(toRoot);
    if (fromIndex === -1 || toIndex === -1) {
        return 0;
    }
    // Calculate the shortest interval (never more than 6 semitones in either direction)
    let interval = toIndex - fromIndex;
    if (interval > 6) {
        interval -= 12;
    }
    else if (interval < -6) {
        interval += 12;
    }
    return interval;
};
